import {
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  Component,
  ViewChild,
  Inject,
  Output,
  EventEmitter,
  Renderer2,
  OnInit,
  Input
} from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, UntypedFormArray } from '@angular/forms';
import { DOCUMENT } from '@angular/common';
import * as _ from 'lodash';

import { DefaultModalComponent } from 'shared/modal/default-modal/default-modal.component';
import { BrandService } from 'shared/brand/brand.service';
import { AuthService } from 'shared/auth/auth.service';
import { segmentData } from 'shared/part-types/segment-data-stub';
import { LoaderComponent } from 'shared/loader/loader.component';
import { AppSummaryService } from 'shared/app-summary/app-summary.service';


@Component({
  moduleId: module.id,
  selector: 'app-summary-modal',
  templateUrl: 'app-summary-modal.component.html',
  styleUrls: ['app-summary-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppSummaryModalComponent implements OnInit {

  @ViewChild(DefaultModalComponent, {static: false}) modal: DefaultModalComponent | void;

  @ViewChild('appSummaryModalLoader', {static: false}) loader: LoaderComponent | void;

  // assigned part terminology
  assigned: Array<any> = [];
  bulkEdit: boolean = false;
  formGroup: UntypedFormGroup;
  formArray: UntypedFormArray;

  @Input() maxLength: number = 2000;

  @Output() hidden: EventEmitter<any> = new EventEmitter();

  @Output() onAppSummaryCompleted = new EventEmitter<{}>();

  selectedAppSummaryTemplate = {id: null, template: null, value: null};

  options: Array<any> = [
    {id: 0, template: 'Custom', value: ''},
    {id: 1, template: 'Make: Model YYYY - YYYY', value: ''},
    {id: 2, template: 'Make: Model YY - YY Submodel / Submodel / Submodel', value: ''},
    {id: 3, template: 'Make: Model YY - YY X.XX L Engine', value: ''},
    {id: 4, template: 'Make: Model YY - YY X.XX L Engine / Position', value: ''},
  ];

  segmentData = segmentData;
  firstLoad: boolean = true;

  loading: boolean = false;

  constructor(
    protected brandService: BrandService,
    protected ref: ChangeDetectorRef,
    @Inject(DOCUMENT) private document: any,
    private _renderer: Renderer2,
    private authService: AuthService,
    private appSummaryService: AppSummaryService,
  ) {
  }

  ngOnInit() {
    this.selectedAppSummaryTemplate = this.options[0];
    this.formArray = new UntypedFormArray([new UntypedFormControl('')]);
    this.formGroup = new UntypedFormGroup({
      appSummary: this.formArray
    });

  }

  get canContinue() {
    return true;
  }

  get editMode() {
    if (this.selectedAppSummaryTemplate.id === 0) {
      return true;
    } else {
      return false;
    }
  }

  get appSummaryTruncated() {
    return false;
  }

  // public
  public show(itemId: any, appSummary: any = null, bulkEdit: boolean = false) {
    this.bulkEdit = bulkEdit;

    /*
     * Set the Custom Template's value equal to what was passed in to the Modal
     */
    const customAppSummary = _.find(this.options, ['id', 0]);
    if (customAppSummary) {
      customAppSummary.value = _.get(appSummary, 'custom_value', '');
    }

    /*
     * Set the template bassed on the template id provided
     */
    this.selectedAppSummaryTemplate = Object.assign(
        {}, this.selectedAppSummaryTemplate, _.find(this.options, ['id', appSummary.template_id]));

    if (!this.selectedAppSummaryTemplate) {
      this.selectedAppSummaryTemplate = {id: null, template: null, value: null};
    } else {
      const appSummaryValue: Array<string> | string = this.selectedAppSummaryTemplate?.value;
      const appSummaryValueArr: Array<string> = _.isArray(appSummaryValue) ? appSummaryValue : [appSummaryValue];
      this.updateFormValue(appSummaryValueArr);
    }

    if (itemId && !this.bulkEdit) {
      this.appSummaryService.fetch(this.brandService.getBrand(), itemId)
      .subscribe((summaries) => {
        _.each(this.options, (option) => {
          const summary = _.find(summaries, ['template_id', option.id]);

          if (summary) {

            option.value = summary.value;

            if (appSummary.template_id === option.id) {
              const appSummaryValueArr: Array<string> = _.isArray(option.value) ? option.value : [option.value];
              this.updateFormValue(appSummaryValueArr);
            }
          }

        });
        this.ref.markForCheck();
        if (this.modal instanceof DefaultModalComponent) {
          this.modal.show();
        }
      });
    } else {
        this.ref.markForCheck();
      if (this.modal instanceof DefaultModalComponent) {
        this.modal.show();
      }
    }
  }

  public hide() {
    this.hidden.emit();
    if (this.modal instanceof DefaultModalComponent) {
      this.modal.hide();
    }
  }

  public onCompleteClick() {
    const appSummary = {
      template_id: this.selectedAppSummaryTemplate.id,
      app_summary: this.formGroup.controls['appSummary'].value,
    };
    this.onAppSummaryCompleted.emit(appSummary);
    this.hide();
  }

  private onSelectionChange(selected) {
    this.selectedAppSummaryTemplate = Object.assign({}, this.selectedAppSummaryTemplate, selected);

    let field = 'value';
    if (this.bulkEdit && selected.id) {
      field = 'template';
    }

    const appSummaryValueArr: Array<string> = _.isArray(this.selectedAppSummaryTemplate[field]) ?
      this.selectedAppSummaryTemplate[field] : [this.selectedAppSummaryTemplate[field]];
    this.updateFormValue(appSummaryValueArr);
    this.ref.markForCheck();
  }

  private startLoading() {
    this.loading = true;
    if (this.loader instanceof LoaderComponent) {
      this.loader.show();
    }
  }

  private stopLoading() {
    this.loading = false;
    if (this.loader instanceof LoaderComponent) {
      this.loader.hide();
    }
  }

  onDeleteControlClick(control: UntypedFormControl) {
    const formArray: UntypedFormArray = this.formArray;
    if (!formArray) { return console.warn('formArray not found!'); }
    const index: number = formArray.controls.indexOf(control);
    formArray.removeAt(index);
  }

  onAddAnotherClick() {
    const formArray: UntypedFormArray = this.formArray;
    formArray.push(new UntypedFormControl(''));
  }

  updateFormValue(valueArr: Array<string>) {
    this.formArray = new UntypedFormArray([]);
    _.each(valueArr, (v) => {
      this.formArray.push(new UntypedFormControl(v));
    });
    this.formGroup = new UntypedFormGroup({
      appSummary: this.formArray
    });
  }
}
